import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Divider
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  AttachMoney as MoneyIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ShiftViewDialog = ({ 
  open, 
  onClose, 
  task, 
  shift, // Keep for backward compatibility
  employees, 
  departments,
  onEdit,
  onDelete,
  timeFormat = '24hour',
  canEdit = true,
  canDelete = true
}) => {
  const theme = useTheme();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Use task if provided, otherwise fall back to shift for compatibility
  const currentTask = task || shift;
  if (!currentTask) return null;

  const selectedEmployeeData = employees?.find(emp => emp.id === currentTask.employee_id);
  const selectedDepartmentData = departments?.find(dept => dept.id === currentTask.department_id);

  const calculateTaskDuration = () => {
    if (currentTask.start_time && currentTask.end_time) {
      const start = parseISO(`2000-01-01T${currentTask.start_time}`);
      const end = parseISO(`2000-01-01T${currentTask.end_time}`);
      const duration = (end - start) / (1000 * 60); // minutes
      const hours = Math.floor(duration / 60);
      const minutes = duration % 60;
      return `${hours}h ${minutes}m`;
    }
    return '0h 0m';
  };

  const formatTime = (time) => {
    if (!time) return '';
    const parsed = parseISO(`2000-01-01T${time}`);
    return format(parsed, timeFormat === '24hour' ? 'HH:mm' : 'h:mm a');
  };

  const formatDate = (date) => {
    if (!date) return '';
    return format(parseISO(date), 'MMMM d, yyyy');
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (onDelete && currentTask.id) {
      onDelete(currentTask.id);
      setDeleteDialogOpen(false);
      onClose(); // Close the task view dialog after deletion
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        fontWeight: 500, 
        color: 'primary.main',
        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        py: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1,
            borderRadius: 2,
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          }}>
            <ScheduleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
          </Box>
          Task Details
        </Box>
        
        <IconButton onClick={onClose} sx={{ color: 'text.secondary' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, mt: 4 }}>
        <Grid container spacing={3}>
          {/* Employee & Department Info */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              backgroundColor: alpha(theme.palette.primary.main, 0.02),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main', fontSize: '1.1rem' }}>
                Employee Information
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <PersonIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {selectedEmployeeData ? `${selectedEmployeeData.first_name} ${selectedEmployeeData.last_name}` : 'Unknown Employee'}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <BusinessIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {selectedDepartmentData?.name || 'No Department'}
                </Typography>
              </Box>

              {currentTask.job_position && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssignmentIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" color="text.secondary">
                    {currentTask.job_position}
                  </Typography>
                </Box>
              )}
            </Box>
          </Grid>

          {/* Task Details */}
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              backgroundColor: alpha(theme.palette.success.main, 0.02),
              border: `1px solid ${alpha(theme.palette.success.main, 0.1)}`
            }}>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'success.main', fontSize: '1.1rem' }}>
                Task Information
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <ScheduleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {formatDate(currentTask.shift_date)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TimeIcon sx={{ color: 'success.main', fontSize: 20 }} />
                <Typography variant="body2" color="text.secondary">
                  {formatTime(currentTask.start_time)} - {formatTime(currentTask.end_time)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Duration: {calculateTaskDuration()}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Notes */}
          {currentTask.notes && (
            <Grid item xs={12}>
              <Box sx={{ 
                p: 3, 
                borderRadius: 3, 
                backgroundColor: alpha(theme.palette.grey[100], 0.3),
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'text.primary', fontSize: '1.1rem' }}>
                  Notes
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                  {currentTask.notes}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Role Documentation - Compiled Markdown */}
          {currentTask.role_documentation && (
            <Grid item xs={12}>
              <Box sx={{ 
                borderRadius: 3, 
                overflow: 'hidden',
                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
              }}>
                <Box sx={{ 
                  p: 3, 
                  pb: 2,
                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      p: 1.5,
                      borderRadius: 3,
                      background: `linear-gradient(${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AssignmentIcon sx={{ fontSize: 28, color: 'primary.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 500, 
                      color: 'primary.main',
                      fontSize: '1.2rem',
                      letterSpacing: '0.5px'
                    }}>
                      Primary Responsibilities
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ 
                  p: 3,
                  backgroundColor: 'white',
                  maxHeight: '60vh',
                  overflow: 'auto'
                }}>
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      h1: ({ children }) => (
                        <Typography variant="h4" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                          {children}
                        </Typography>
                      ),
                      h2: ({ children }) => (
                        <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, mt: 3, color: 'primary.main' }}>
                          {children}
                        </Typography>
                      ),
                      h3: ({ children }) => (
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, mt: 2.5, color: 'text.primary' }}>
                          {children}
                        </Typography>
                      ),
                      p: ({ children }) => (
                        <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.7, color: 'text.primary' }}>
                          {children}
                        </Typography>
                      ),
                      ul: ({ children }) => (
                        <Box component="ul" sx={{ pl: 3, mb: 2 }}>
                          {children}
                        </Box>
                      ),
                      ol: ({ children }) => (
                        <Box component="ol" sx={{ pl: 3, mb: 2 }}>
                          {children}
                        </Box>
                      ),
                      li: ({ children }) => (
                        <Typography component="li" variant="body1" sx={{ mb: 0.5, lineHeight: 1.6 }}>
                          {children}
                        </Typography>
                      ),
                      blockquote: ({ children }) => (
                        <Box sx={{
                          borderLeft: `4px solid ${theme.palette.primary.main}`,
                          backgroundColor: alpha(theme.palette.primary.main, 0.05),
                          p: 2,
                          my: 2,
                          borderRadius: 1
                        }}>
                          {children}
                        </Box>
                      ),
                      code: ({ inline, className, children, ...props }) => {
                        if (inline) {
                          return (
                            <code
                              style={{
                                backgroundColor: alpha(theme.palette.grey[100], 0.8),
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                                fontSize: '0.85em',
                                color: theme.palette.text.primary
                              }}
                              {...props}
                            >
                              {children}
                            </code>
                          );
                        }
                        return (
                          <pre
                            style={{
                              backgroundColor: alpha(theme.palette.grey[900], 0.05),
                              border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                              borderRadius: '8px',
                              padding: '16px',
                              margin: '16px 0',
                              overflow: 'auto',
                              fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                              fontSize: '0.9em',
                              lineHeight: '1.6',
                              color: theme.palette.text.primary,
                              whiteSpace: 'pre',
                              tabSize: 4
                            }}
                          >
                            <code {...props}>{children}</code>
                          </pre>
                        );
                      },
                      table: ({ children }) => (
                        <Box sx={{ overflowX: 'auto', my: 2 }}>
                          <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                          }}>
                            {children}
                          </table>
                        </Box>
                      ),
                      th: ({ children }) => (
                        <th style={{
                          padding: '12px',
                          backgroundColor: alpha(theme.palette.primary.main, 0.1),
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
                          fontWeight: 500,
                          textAlign: 'left'
                        }}>
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td style={{
                          padding: '12px',
                          border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                        }}>
                          {children}
                        </td>
                      ),
                      hr: () => (
                        <Divider sx={{ my: 3 }} />
                      )
                    }}
                  >
                    {currentTask.role_documentation}
                  </ReactMarkdown>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
        <Box />
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Close
          </Button>
          {canEdit && onEdit && (
            <Button
              onClick={() => onEdit(currentTask)}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3,
                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
              }}
            >
              Edit Task
            </Button>
          )}
          {canDelete && onDelete && (
            <Button
              onClick={handleDeleteClick}
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              sx={{ 
                borderRadius: 2, 
                px: 3,
                boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
              }}
            >
              Delete Task
            </Button>
          )}
        </Box>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: `0 8px 32px ${alpha(theme.palette.grey[900], 0.15)}`
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: 500, 
          color: 'error.main',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
          py: 3
        }}>
          Delete Task
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this task?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. The task for {selectedEmployeeData ? `${selectedEmployeeData.first_name} ${selectedEmployeeData.last_name}` : 'Unknown Employee'} on {formatDate(currentTask.shift_date)} will be permanently removed.
          </Typography>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3,
              boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.3)}`
            }}
          >
            Delete Task
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default ShiftViewDialog;